/**
 * `/recruiting/interviews` — the Interviews screen, WP-W4.
 * `web/shapes/interviews.md` is the agreed shape, written before this file.
 *
 * The fourth recruiting surface, on the merged W1/W2/W3 foundation. It consumes the
 * interviews API (schedule / list / get / patch / panel / scorecard / list-scorecards)
 * and serves two personas at once, gated by the anyOf the menu item declares:
 *
 *  - the **coordinator** (`recruiting.view_candidates`, and `manage_candidates` to
 *    write) schedules interviews, patches their status, seats and unseats the panel,
 *    and reads every scorecard;
 *  - the **interviewer** (`recruiting.submit_feedback`) finds the interviews they sit
 *    on (`mine=true`) and files their own scorecard.
 *
 * ── The scorecard interviewer is the session employee ───────────────────────
 *
 * The one invariant the task turns on: a scorecard's `interviewer_employee_id` is the
 * session employee, stamped server-side, and is **never a form field**. The scorecard
 * form has no "who" input; a caller cannot submit as someone else. A `submit_feedback`
 * holder who is not on the panel is refused `403 WORKFORCE_SCOPE_DENIED`, surfaced as a
 * plain "you are not on this interview's panel" rather than the generic denial.
 *
 * ── Names are resolved, not carried ─────────────────────────────────────────
 *
 * `RecruitingInterview` carries an `application_id`, not the candidate's name. The name
 * is resolved from the applications and candidates lists the screen reads on
 * `recruiting.view_candidates`; a `submit_feedback`-only session lacks that permission,
 * so those reads are refused and the application falls back to its id — the same honest
 * fallback the pipeline uses. Each read is an independent region: a refusal on one
 * leaves the rest standing (`PRODUCT.md` §4).
 */

import { useMemo, useState, type ReactNode } from 'react';

import { useI18n } from '../../i18n';
import { formatInstant } from '../../i18n/datetime';
import { useSession } from '../../auth/SessionProvider';
import type {
  InterviewStatus,
  RecruitingApplicationListResponse,
  RecruitingCandidateListResponse,
  RecruitingInterview,
  RecruitingInterviewListResponse,
  RecruitingInterviewPatchRequest,
  RecruitingInterviewScheduleRequest,
  RecruitingPanelMemberListResponse,
  RecruitingPanelMutateRequest,
  RecruitingScorecard,
  RecruitingScorecardListResponse,
  RecruitingScorecardSubmitRequest,
  PanelRole,
} from '../../api/recruiting';
import {
  recruitingApplicationInterviewsPath,
  recruitingApplicationsPath,
  recruitingCandidatesPath,
  recruitingInterviewPanelPath,
  recruitingInterviewPath,
  recruitingInterviewScorecardPath,
  recruitingInterviewScorecardsPath,
  recruitingInterviewsPath,
} from '../../api/recruiting';
import { useApiMutation } from '../../api/useApiMutation';
import { useApiQuery } from '../../api/useApiQuery';
import {
  INTERVIEW_KIND_LABEL,
  INTERVIEW_STATUS_BADGE,
  INTERVIEW_STATUS_LABEL,
} from '../../recruiting/interview';
import { ApiErrorNote } from '../../states/ApiErrorNote';
import { Badge } from '../../states/Badge';
import { EmptyState } from '../../states/EmptyState';
import { PartialData } from '../../states/PartialData';
import { Skeleton } from '../../states/Skeleton';
import { SEVERITY_ICON } from '../../states/icons';
import { ScreenHeading } from '../../shell/ScreenHeading';
import { ScheduleInterviewForm } from './ScheduleInterviewForm';
import { InterviewStatusControls } from './InterviewStatusControls';
import { InterviewPanelPanel } from './InterviewPanelPanel';
import { ScorecardPanel } from './ScorecardPanel';
import type { PickerOption } from './CreateApplicationForm';

const PAGE_LIMIT = 50;
const DEFAULT_TIMEZONE = 'Asia/Ho_Chi_Minh';

export function InterviewsScreen(): ReactNode {
  const { t, locale } = useI18n();
  const { user } = useSession();
  const timezone = user?.timezone ?? DEFAULT_TIMEZONE;
  const meId = user?.employee_id ?? '';
  const meLabel = user?.display_name ?? meId;
  const canViewAll = (user?.permissions ?? []).includes('recruiting.view_candidates');

  const [mine, setMine] = useState(false);
  const [applicationFilter, setApplicationFilter] = useState('');
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [listToken, setListToken] = useState(0);
  const [allToken, setAllToken] = useState(0);

  const trimmedFilter = applicationFilter.trim();
  const listPath = recruitingInterviewsPath({
    limit: PAGE_LIMIT,
    application_id: trimmedFilter === '' ? undefined : trimmedFilter,
    mine: mine ? 'true' : undefined,
  });
  const list = useApiQuery<RecruitingInterviewListResponse>(listPath, listToken);

  // Enrichment reads for name resolution and the pickers. Independent regions on
  // `recruiting.view_candidates`; a `submit_feedback`-only session sees them refused and
  // falls back to ids.
  const applications = useApiQuery<RecruitingApplicationListResponse>(
    recruitingApplicationsPath({ limit: PAGE_LIMIT }),
  );
  const candidates = useApiQuery<RecruitingCandidateListResponse>(
    recruitingCandidatesPath({ limit: PAGE_LIMIT }),
  );

  const allPath =
    canViewAll && selectedId !== null ? recruitingInterviewScorecardsPath(selectedId) : null;
  const allScorecardsQuery = useApiQuery<RecruitingScorecardListResponse>(allPath, allToken);

  const schedule = useApiMutation<RecruitingInterviewScheduleRequest, RecruitingInterview>(
    recruitingApplicationInterviewsPath('unset'),
    'POST',
  );
  const patchStatus = useApiMutation<RecruitingInterviewPatchRequest, RecruitingInterview>(
    recruitingInterviewPath('unset'),
    'PATCH',
  );
  const panelMutate = useApiMutation<
    RecruitingPanelMutateRequest,
    RecruitingPanelMemberListResponse
  >(recruitingInterviewPanelPath('unset'), 'POST');
  const scorecardSubmit = useApiMutation<RecruitingScorecardSubmitRequest, RecruitingScorecard>(
    recruitingInterviewScorecardPath('unset'),
    'POST',
  );

  const rows: readonly RecruitingInterview[] = useMemo(() => list.data?.data ?? [], [list.data]);
  const hasMore = list.data?.page.has_more ?? false;

  // application_id → candidate_id, from the applications list.
  const applicationCandidate = useMemo(() => {
    const map = new Map<string, string>();
    for (const app of applications.data?.data ?? []) map.set(app.id, app.candidate_id);
    return map;
  }, [applications.data]);

  // candidate_id → name, from the candidates list.
  const candidateName = useMemo(() => {
    const map = new Map<string, string>();
    for (const cand of candidates.data?.data ?? []) map.set(cand.id, cand.full_name);
    return map;
  }, [candidates.data]);

  const applicationLabel = (applicationId: string): string => {
    const candId = applicationCandidate.get(applicationId);
    const name = candId === undefined ? undefined : candidateName.get(candId);
    return name ?? applicationId;
  };

  // The picker/filter options: the applications list, labelled by candidate name where
  // resolvable. `null` when the list is out of reach — the fields become id inputs.
  const applicationOptions: readonly PickerOption[] | null = useMemo(() => {
    if (applications.status !== 'ok' || applications.data === null) return null;
    return applications.data.data.map((app) => ({
      id: app.id,
      label: candidateName.get(app.candidate_id) ?? app.id,
    }));
  }, [applications.status, applications.data, candidateName]);

  const selected = selectedId === null ? null : (rows.find((row) => row.id === selectedId) ?? null);

  // The caller's own scorecard: the submit response wins (it is the freshest), else the
  // caller's row from the readable all-list.
  const ownScorecard: RecruitingScorecard | null =
    scorecardSubmit.status === 'ok'
      ? scorecardSubmit.data
      : (allScorecardsQuery.data?.data.find((c) => c.interviewer_employee_id === meId) ?? null);

  const panel = panelMutate.status === 'ok' ? (panelMutate.data?.data ?? null) : null;

  const submitSchedule = (
    applicationId: string,
    body: RecruitingInterviewScheduleRequest,
  ): void => {
    schedule
      .submit(body, recruitingApplicationInterviewsPath(applicationId))
      .then(() => {
        setListToken((value) => value + 1);
      })
      .catch(() => {
        /* `submit` resolves rather than rejects. */
      });
  };

  const moveStatus = (to: InterviewStatus): void => {
    if (selectedId === null || to === 'scheduled') return;
    patchStatus
      .submit({ status: to }, recruitingInterviewPath(selectedId))
      .then(() => {
        setListToken((value) => value + 1);
      })
      .catch(() => {
        /* resolves rather than rejects */
      });
  };

  const addPanelist = (employeeId: string, role: PanelRole): void => {
    if (selectedId === null) return;
    panelMutate
      .submit(
        { add: [{ interviewer_employee_id: employeeId, panel_role: role }] },
        recruitingInterviewPanelPath(selectedId),
      )
      .catch(() => {
        /* resolves rather than rejects */
      });
  };

  const removePanelist = (panelistId: string): void => {
    if (selectedId === null) return;
    panelMutate
      .submit({ remove: [panelistId] }, recruitingInterviewPanelPath(selectedId))
      .catch(() => {
        /* resolves rather than rejects */
      });
  };

  const submitScorecard = (body: RecruitingScorecardSubmitRequest): void => {
    if (selectedId === null) return;
    scorecardSubmit
      .submit(body, recruitingInterviewScorecardPath(selectedId))
      .then(() => {
        if (canViewAll) setAllToken((value) => value + 1);
      })
      .catch(() => {
        /* resolves rather than rejects */
      });
  };

  const openInterview = (id: string): void => {
    patchStatus.reset();
    panelMutate.reset();
    scorecardSubmit.reset();
    setSelectedId(id);
  };

  return (
    <div className="wp-stack wp-f">
      <ScreenHeading>{t('nav.interviews')}</ScreenHeading>
      <div className="wp-f-lede">
        <p className="wp-small wp-muted">{t('recruiting.interviews.intro')}</p>
        <p className="wp-small wp-muted" data-testid="w4-scope-note">
          {t('recruiting.interviews.scopeNote')}
        </p>
      </div>

      <div className="wp-filters">
        <div className="wp-filters__group">
          <label className="wp-field__label" htmlFor="w4-application-filter">
            {t('recruiting.interviews.filter.application')}
          </label>
          {applicationOptions === null ? (
            <>
              <input
                className="wp-field__input"
                id="w4-application-filter"
                data-testid="w4-application-filter"
                type="text"
                value={applicationFilter}
                autoComplete="off"
                onChange={(event) => {
                  setApplicationFilter(event.target.value);
                  setSelectedId(null);
                }}
              />
              <p className="wp-small wp-muted">
                {t('recruiting.interviews.filter.applicationIdHint')}
              </p>
            </>
          ) : (
            <select
              className="wp-field__input"
              id="w4-application-filter"
              data-testid="w4-application-filter"
              value={applicationFilter}
              onChange={(event) => {
                setApplicationFilter(event.target.value);
                setSelectedId(null);
              }}
            >
              <option value="">{t('filters.all')}</option>
              {applicationOptions.map((option) => (
                <option key={option.id} value={option.id}>
                  {option.label}
                </option>
              ))}
            </select>
          )}
        </div>
        <div className="wp-filters__group">
          <label className="wp-field__label" htmlFor="w4-mine-toggle">
            {t('recruiting.interviews.filter.mine')}
          </label>
          <input
            id="w4-mine-toggle"
            data-testid="w4-mine-toggle"
            type="checkbox"
            checked={mine}
            onChange={(event) => {
              setMine(event.target.checked);
              setSelectedId(null);
            }}
          />
          <p className="wp-small wp-muted">{t('recruiting.interviews.filter.mineHint')}</p>
        </div>
      </div>

      {list.status === 'loading' ? (
        <Skeleton
          rows={4}
          variant="block"
          label={t('recruiting.interviews.subjectWord')}
          testId="w4-list-skeleton"
        />
      ) : null}
      {list.status === 'error' && list.error !== null ? (
        <ApiErrorNote
          error={list.error}
          onRetry={list.reload}
          subject={t('recruiting.interviews.subjectWord')}
          testId="w4-list-error"
        />
      ) : null}

      {list.data === null ? null : rows.length === 0 ? (
        <EmptyState
          title={t('recruiting.interviews.empty.title')}
          description={t('recruiting.interviews.empty.body')}
          testId="w4-list-empty"
        />
      ) : (
        <>
          {hasMore ? (
            <PartialData
              excludes={t('recruiting.interviews.partial.excludes', { shown: rows.length })}
              testId="w4-list-partial"
            />
          ) : null}
          <ul className="wp-rows" data-testid="w4-list">
            {rows.map((interview) => (
              <li
                className="wp-row"
                key={interview.id}
                data-testid={`w4-interview-${interview.id}`}
              >
                <div className="wp-row__main">
                  <p>
                    <Badge
                      severity={INTERVIEW_STATUS_BADGE[interview.status]}
                      icon={SEVERITY_ICON[INTERVIEW_STATUS_BADGE[interview.status]]}
                      label={t(INTERVIEW_STATUS_LABEL[interview.status])}
                      testId={`w4-interview-status-${interview.id}`}
                    />{' '}
                    <span data-testid={`w4-interview-name-${interview.id}`}>
                      {applicationLabel(interview.application_id)}
                    </span>
                  </p>
                  <p className="wp-small wp-muted">
                    {t('recruiting.interviews.row.meta', {
                      kind:
                        interview.kind === null
                          ? t('recruiting.interviews.kind.none')
                          : t(INTERVIEW_KIND_LABEL[interview.kind]),
                      when:
                        interview.scheduled_start === null
                          ? t('recruiting.interviews.row.unscheduled')
                          : formatInstant(interview.scheduled_start, locale, timezone),
                    })}
                  </p>
                </div>
                <div className="wp-row__figure">
                  <button
                    className="wp-button"
                    type="button"
                    data-testid={`w4-open-${interview.id}`}
                    onClick={() => {
                      openInterview(interview.id);
                    }}
                  >
                    {t('recruiting.interviews.open')}
                  </button>
                </div>
              </li>
            ))}
          </ul>
        </>
      )}

      {selected === null ? null : (
        <section className="wp-section" aria-labelledby="w4-detail-heading" data-testid="w4-detail">
          <h2 className="wp-section__title" id="w4-detail-heading" tabIndex={-1}>
            {applicationLabel(selected.application_id)}
          </h2>

          <dl className="wp-deflist" data-testid="w4-detail-fields">
            <Field
              label={t('recruiting.interviews.detail.status')}
              value={t(INTERVIEW_STATUS_LABEL[selected.status])}
            />
            <Field
              label={t('recruiting.interviews.detail.kind')}
              value={selected.kind === null ? null : t(INTERVIEW_KIND_LABEL[selected.kind])}
            />
            <Field
              label={t('recruiting.interviews.detail.start')}
              value={
                selected.scheduled_start === null
                  ? null
                  : formatInstant(selected.scheduled_start, locale, timezone)
              }
            />
            <Field
              label={t('recruiting.interviews.detail.end')}
              value={
                selected.scheduled_end === null
                  ? null
                  : formatInstant(selected.scheduled_end, locale, timezone)
              }
            />
            <Field label={t('recruiting.interviews.detail.timezone')} value={selected.timezone} />
            <Field label={t('recruiting.interviews.detail.location')} value={selected.location} />
            <Field
              label={t('recruiting.interviews.detail.meetingUrl')}
              value={selected.meeting_url}
            />
          </dl>

          <section
            className="wp-section__subsection"
            aria-labelledby="w4-status-heading"
            data-testid="w4-status"
          >
            <h3 className="wp-section__title" id="w4-status-heading">
              {t('recruiting.interviews.status.title')}
            </h3>
            <InterviewStatusControls
              status={selected.status}
              pending={patchStatus.status === 'pending'}
              error={patchStatus.error}
              onMove={moveStatus}
            />
          </section>

          <InterviewPanelPanel
            panel={panel}
            pending={panelMutate.status === 'pending'}
            error={panelMutate.error}
            onAdd={addPanelist}
            onRemove={removePanelist}
          />

          <ScorecardPanel
            meLabel={meLabel}
            ownScorecard={ownScorecard}
            submitPending={scorecardSubmit.status === 'pending'}
            submitError={scorecardSubmit.error}
            onSubmit={submitScorecard}
            canViewAll={canViewAll}
            allStatus={allScorecardsQuery.status}
            allScorecards={allScorecardsQuery.data?.data ?? null}
            allError={allScorecardsQuery.error}
            onReloadAll={allScorecardsQuery.reload}
            locale={locale}
            timezone={timezone}
          />
        </section>
      )}

      {/* The create form is the last section, not the first: a recruiter opens this
          screen to read the list, and opening a new one is the occasional action.
          Leading with the form put the subject of the page below the fold. */}
      <ScheduleInterviewForm
        applicationOptions={applicationOptions}
        defaultTimezone={timezone}
        pending={schedule.status === 'pending'}
        error={schedule.error}
        created={schedule.status === 'ok' ? schedule.data : null}
        onSubmit={submitSchedule}
      />
    </div>
  );
}

/** One definition-list row; a null value reads as the em-dash placeholder, never blank. */
function Field({
  label,
  value,
}: {
  readonly label: string;
  readonly value: string | null;
}): ReactNode {
  const { t } = useI18n();
  return (
    <>
      <dt className="wp-small wp-muted">{label}</dt>
      <dd>{value === null || value === '' ? t('recruiting.interviews.detail.none') : value}</dd>
    </>
  );
}
